/**
 * EAA-1813: shell-chrome API client — global search (⌘K) and the per-user
 * notification centre. These are the two topbar controls that shipped
 * `disabled` with an sr-only "chưa khả dụng" note because no endpoint existed;
 * EAA-1831 added both server-side, so this file is what un-inerts them.
 *
 * Same conventions as `console.ts` / `orgAdmin.ts`: plain fetch, `{success,
 * code, data}` envelope unwrap, the shared typed 401 so callers re-run
 * `login()` instead of rendering a failure.
 *
 * It keeps its OWN `request` (rather than importing console.ts's) for one
 * concrete reason: neither surface is acting-org scoped, so neither may send
 * `X-Org-Id`. Search narrows itself server-side from the caller's own admin
 * grants (`IsPortalAdmin` + `admin_org_ids`) and never calls
 * `resolve_acting_org`; notifications are keyed on the Keycloak `sub` alone.
 * portal-api ignores the header on both routes, so sending it would only
 * misdescribe what the request is scoped to.
 */

import { OrgAdminApiError, OrgAdminUnauthorizedError } from '@/api/orgAdmin';

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    request_id?: string;
    data: T;
}

/** `emg_shared.api.common.paginate_queryset` — NOT DRF's `{count,next,previous}`. */
export interface Paginated<T> {
    results: T[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

async function request<T>(
    apiBase: string,
    token: string,
    path: string,
    init?: { method?: string }
): Promise<T> {
    const res = await fetch(`${apiBase}${path}`, {
        method: init?.method ?? 'GET',
        headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
        throw new OrgAdminUnauthorizedError();
    }
    const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
    if (!res.ok || !envelope?.success) {
        throw new OrgAdminApiError(
            envelope?.description ?? `Request failed (${res.status})`,
            envelope?.code ?? res.status,
            (envelope as { data?: unknown } | null)?.data
        );
    }
    return envelope.data;
}

/* ---- Global search (GET /api/v1/search) ---------------------------------
   Shapes mirror `GlobalSearchService` in
   services/portal-api/portal_api/directory/services.py (~L1869-2240) field for
   field. Every group carries a ready-made FE route in `target` — there is no
   `type`, `label`, `subtitle` or `url` field, and no flat `results[]` array:
   the payload is strictly the five-key grouped dict below, all five keys
   always present. `q` is the only query param the view reads; the per-group
   cap (10) and the 2-character minimum are server-fixed, not negotiable. */

export interface SearchOrganization {
    id: string;
    code: string;
    name: string;
    target: string;
}

export interface SearchTenant {
    id: string;
    /** The ACH tenant id; `id` is the OrganizationAppScope row id. */
    scope_id: string;
    name: string;
    organization_id: string;
    organization_code: string;
    application_code: string;
    target: string;
}

export interface SearchApplication {
    id: string;
    code: string;
    name: string;
    enabled: boolean;
    target: string;
}

export interface SearchLevel {
    id: string;
    name: string;
    role_key: string;
    organization_id: string;
    organization_code: string;
    target: string;
}

export interface SearchAccount {
    /** Accounts have NO `id` — `user_sub` is the identifier. */
    user_sub: string;
    user_email: string;
    organization_id: string;
    target: string;
}

export interface GlobalSearchResults {
    organizations: SearchOrganization[];
    tenants: SearchTenant[];
    applications: SearchApplication[];
    levels: SearchLevel[];
    accounts: SearchAccount[];
}

export interface GlobalSearchResponse {
    query: string;
    results: GlobalSearchResults;
}

/** `GlobalSearchService.MIN_QUERY_LENGTH` — below this the server returns all
 *  five groups empty, so the client must not spend a request on it. */
export const SEARCH_MIN_QUERY_LENGTH = 2;

/** Server-side `RESULTS_PER_TYPE`; surfaced so the UI can say a group is capped. */
export const SEARCH_RESULTS_PER_TYPE = 10;

export function fetchGlobalSearch(
    apiBase: string,
    token: string,
    query: string
): Promise<GlobalSearchResponse> {
    const params = new URLSearchParams({ q: query });
    return request<GlobalSearchResponse>(apiBase, token, `/api/v1/search?${params.toString()}`);
}

/* ---- Notifications (per-user, IsAuthenticated) --------------------------
   `NotificationSerializer` (org_serializers.py ~L490) exposes exactly the nine
   fields below. Unread state is `is_read: false` — there is NO severity,
   level, priority or status field on the model, and no dismiss/delete route. */

export type NotificationType =
    | 'role_map_paused'
    | 'assignment_sync_error'
    | 'app_pending_approval'
    | 'orphan_level';

export interface PortalNotification {
    id: string;
    notification_type: NotificationType;
    title: string;
    /** TextField, defaults to `''` — never null, but often empty. */
    body: string;
    /** The FE route this notification resolves at; `''` when it has none. */
    target: string;
    payload: Record<string, unknown>;
    is_read: boolean;
    /** Null while unread. */
    read_at: string | null;
    created_at: string;
}

export function fetchNotifications(
    apiBase: string,
    token: string,
    params?: { unreadOnly?: boolean; page?: number; pageSize?: number }
): Promise<Paginated<PortalNotification>> {
    const query = new URLSearchParams();
    if (params?.unreadOnly) query.set('unread_only', 'true');
    if (params?.page && params.page > 1) query.set('page', String(params.page));
    if (params?.pageSize) query.set('page_size', String(params.pageSize));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<Paginated<PortalNotification>>(
        apiBase,
        token,
        `/api/v1/me/notifications${suffix}`
    );
}

/** The ONLY place an unread count exists — the list response carries no count.
 *  Cached 30s server-side and invalidated asynchronously on mark-read, so a
 *  refetch fired immediately after a mutation can briefly still be stale. */
export function fetchNotificationSummary(
    apiBase: string,
    token: string
): Promise<{ unread_count: number }> {
    return request<{ unread_count: number }>(apiBase, token, '/api/v1/me/notifications/summary');
}

/** POST, no body — the id comes from the path. Idempotent; 404 (code 3000) for
 *  an unknown id OR another user's id (deliberately not 403: no existence leak). */
export function markNotificationRead(
    apiBase: string,
    token: string,
    notificationId: string
): Promise<PortalNotification> {
    return request<PortalNotification>(
        apiBase,
        token,
        `/api/v1/me/notifications/${encodeURIComponent(notificationId)}/read`,
        { method: 'POST' }
    );
}

/** POST, no body. `updated` is how many unread rows were flipped (0 when clean). */
export function markAllNotificationsRead(
    apiBase: string,
    token: string
): Promise<{ updated: number }> {
    return request<{ updated: number }>(apiBase, token, '/api/v1/me/notifications/read-all', {
        method: 'POST',
    });
}
